/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Lightbulb, ChevronDown, ChevronUp, X, Music, Guitar } from 'lucide-react';
import { Button } from './ui/button';

interface MobileLearningPanelProps {
  appMode: 'scale' | 'chord' | 'earTraining' | 'compare';
  currentKey?: string;
  currentScale?: string;
  onDismiss?: () => void;
}

/**
 * MobileLearningPanel - Combined tutorial and hints content for mobile
 *
 * Displays below Jam Tracks (Scale mode) or Fingerings (Chord mode)
 * Combines educational content from TheoryCard and contextual tips
 */
const MobileLearningPanel: React.FC<MobileLearningPanelProps> = ({
  appMode,
  currentKey,
  currentScale,
  onDismiss
}) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    // Check localStorage for user preference, default to expanded on first visit
    const saved = localStorage.getItem('mobileLearningPanelExpanded');
    return saved === null ? true : saved === 'true';
  });

  // Save expansion state to localStorage
  useEffect(() => {
    localStorage.setItem('mobileLearningPanelExpanded', String(isExpanded));
  }, [isExpanded]);

  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss();
    }
    // Also save to localStorage
    localStorage.setItem('mobileLearningPanelDismissed', 'true');
  };

  // Mode-specific content
  const getModeContent = () => {
    if (appMode === 'scale') {
      return {
        icon: <Music className="h-4 w-4" />,
        title: 'Scale Mode Tips',
        tips: [
          {
            heading: 'See the Pattern',
            text: 'Notice how the same color pattern appears in multiple places on the fretboard? That\'s the key insight - these are moveable shapes you can play anywhere.'
          },
          {
            heading: 'Try This',
            text: 'Change the key and watch the entire pattern shift together. This is how professionals navigate the fretboard - by recognizing shapes, not memorizing positions.'
          },
          {
            heading: 'Explore Chords',
            text: 'Switch to Chord Mode to see fingerings for chords built from this scale. Understanding the relationship between scales and chords is key to musical freedom.'
          },
          {
            heading: 'Ask the Harmonic Companion',
            text: 'Tap the chat icon for AI-powered insights about your scale - learn its character, common chord progressions, and how to use it musically.'
          }
        ]
      };
    } else if (appMode === 'chord') {
      return {
        icon: <Guitar className="h-4 w-4" />,
        title: 'Chord Mode Tips',
        tips: [
          {
            heading: 'All Chord Tones',
            text: 'The fretboard shows every place you can play each note of the chord. Tap a fingering above to see common voicings professionals use.'
          },
          {
            heading: 'Moveable Shapes',
            text: 'Each chord fingering is a moveable shape - the pattern stays the same, just slide it to different frets to change keys. This is how you learn chords fast.'
          },
          {
            heading: 'Compare Voicings',
            text: 'Try different fingerings to hear how the same chord can sound bright (high position) or full (low position). Position matters for tone and context.'
          },
          {
            heading: 'Explore Scales',
            text: 'Switch to Scale Mode to see which scales work with this chord. Knowing the parent scale unlocks improvisation possibilities.'
          },
          {
            heading: 'Get Deep Insights',
            text: 'Use the Harmonic Companion chat to learn about chord progressions, substitutions, and the musical context of your chord choices.'
          }
        ]
      };
    }
    return null;
  };

  const content = getModeContent();
  if (!content) return null;

  return (
    <div className="w-full mt-3 mb-16 px-3">
      <Card className="bg-gradient-to-br from-blue-50/80 to-indigo-50/80 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200/60 dark:border-blue-800/40 shadow-sm">
        <CardContent className="p-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0 p-1.5 rounded-md bg-blue-500/10">
                <Lightbulb className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                Learning Tips
              </h3>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-7 w-7 p-0 hover:bg-blue-100/50 dark:hover:bg-blue-900/30"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="h-7 w-7 p-0 hover:bg-blue-100/50 dark:hover:bg-blue-900/30"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Collapsed Preview */}
          {!isExpanded && (
            <p className="text-xs text-muted-foreground">
              Tap to see tips for getting the most out of {appMode === 'scale' ? 'Scale' : 'Chord'} Mode
            </p>
          )}

          {/* Expanded Content */}
          {isExpanded && (
            <div className="space-y-3 mt-3">
              {/* Core Theory */}
              <div className="pb-3 border-b border-blue-200/50 dark:border-blue-800/30">
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-shrink-0 mt-0.5">
                    {content.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      Why Visual Patterns Matter
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                      The guitar fretboard can feel overwhelming - but music theory reveals it's really just repeating patterns.
                      When you see these patterns visually, complex theory becomes intuitive.
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      <span className="font-semibold text-blue-800 dark:text-blue-200">The Professional Secret:</span> Great guitarists don't memorize hundreds of positions. They recognize moveable shapes and interval relationships.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mode-Specific Tips */}
              <div className="space-y-2.5">
                {content.tips.map((tip, index) => (
                  <div key={index}>
                    <span className="text-xs font-semibold text-blue-800 dark:text-blue-200">
                      {tip.heading}:
                    </span>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                      {tip.text}
                    </p>
                  </div>
                ))}
              </div>

              {/* Call to Action */}
              <div className="pt-2 border-t border-blue-200/50 dark:border-blue-800/30">
                <p className="text-xs text-muted-foreground leading-relaxed italic">
                  Don't just look - pick up your guitar and play along! Active practice turns visual patterns into muscle memory.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileLearningPanel;
